import { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

const ADMIN_EMAILS = ['jussi@concept.zone']; // Fallback for backward compatibility

export function useAdmin() {
    const { user } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkedUserId, setCheckedUserId] = useState<string | null>(null);

    // Loading is true if we haven't checked this user yet
    const loading = user ? user.uid !== checkedUserId : false;

    useEffect(() => {
        async function checkAdminStatus() {
            if (!user) {
                console.log('[useAdmin] No user, setting isAdmin to false');
                setIsAdmin(false);
                setCheckedUserId(null);
                return;
            }

            // If we've already checked this user, don't check again
            if (user.uid === checkedUserId) {
                console.log('[useAdmin] Already checked this user, skipping');
                return;
            }

            console.log('[useAdmin] Checking admin status for:', user.email, 'UID:', user.uid);

            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    console.log('[useAdmin] User doc exists, isAdmin field:', userData?.isAdmin);
                    // Check both isAdmin field and hardcoded email list for backward compatibility
                    const adminStatus = userData?.isAdmin === true ||
                        (user.email ? ADMIN_EMAILS.includes(user.email) : false);
                    console.log('[useAdmin] Admin status result:', adminStatus, '(from isAdmin field or email list)');
                    setIsAdmin(adminStatus);
                } else {
                    // If no user doc, check hardcoded list
                    console.log('[useAdmin] No user doc found, checking hardcoded email list');
                    const adminStatus = user.email ? ADMIN_EMAILS.includes(user.email) : false;
                    console.log('[useAdmin] Admin status from hardcoded list:', adminStatus);
                    setIsAdmin(adminStatus);
                }
            } catch (error) {
                console.error('[useAdmin] Error checking admin status:', error);
                // Fallback to hardcoded list on error
                const adminStatus = user.email ? ADMIN_EMAILS.includes(user.email) : false;
                console.log('[useAdmin] Admin status from fallback:', adminStatus);
                setIsAdmin(adminStatus);
            } finally {
                // Mark this user as checked
                setCheckedUserId(user.uid);
            }
        }

        checkAdminStatus();
    }, [user, checkedUserId]);

    console.log('[useAdmin] Returning - loading:', loading, 'isAdmin:', isAdmin, 'user.uid:', user?.uid, 'checkedUserId:', checkedUserId);

    return {
        isAdmin,
        loading,
    };
}